export type ShopifyCdnImageOptions = {
  width?: number;
  height?: number;
  crop?: "top" | "center" | "bottom" | "left" | "right" | "region";
};

function isShopifyCdnImageHostPath(hostname: string, pathname: string): boolean {
  if (hostname === "cdn.shopify.com") return true;
  if (hostname.endsWith(".myshopify.com") && pathname.startsWith("/cdn/"))
    return true;
  // Storefront-domain CDN paths, e.g. `//solterra.com/cdn/shop/files/x.png`.
  if (pathname.startsWith("/cdn/shop/")) return true;
  return false;
}

function stripHash(url: string): { base: string; hash: string } {
  const i = url.indexOf("#");
  if (i === -1) return { base: url, hash: "" };
  return { base: url.slice(0, i), hash: url.slice(i) };
}

/**
 * Appends Shopify CDN image transform query params (same shape as Liquid's `image_url`).
 * No-op for non-Shopify URLs, relative URLs, SVGs, or invalid input.
 */
export function shopifyCdnImageUrl(
  url: string,
  options: ShopifyCdnImageOptions
): string {
  const trimmed = url?.trim();
  if (!trimmed) return url;

  const lower = trimmed.toLowerCase();
  if (lower.endsWith(".svg")) return trimmed;

  const { width, height, crop } = options;
  if (
    (width == null || width <= 0) &&
    (height == null || height <= 0)
  ) {
    return trimmed;
  }

  const isAbsolute =
    /^https?:\/\//i.test(trimmed) || trimmed.startsWith("//");
  if (!isAbsolute) return trimmed;

  const protocolRelative = trimmed.startsWith("//");
  const { base, hash } = stripHash(trimmed);
  const parseTarget = protocolRelative ? `https:${base}` : base;

  let parsed: URL;
  try {
    parsed = new URL(parseTarget);
  } catch {
    return trimmed;
  }

  if (!isShopifyCdnImageHostPath(parsed.hostname, parsed.pathname)) {
    return trimmed;
  }

  const params = parsed.searchParams;
  if (width != null && width > 0) params.set("width", String(Math.round(width)));
  if (height != null && height > 0)
    params.set("height", String(Math.round(height)));
  if (crop != null) params.set("crop", crop);

  const qs = params.toString();
  const pathAndQuery = qs ? `${parsed.pathname}?${qs}` : parsed.pathname;

  if (protocolRelative) {
    return `//${parsed.host}${pathAndQuery}${hash}`;
  }
  return `${parsed.origin}${pathAndQuery}${hash}`;
}
